import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import { Modal, Button, Stack, ButtonGroup, Input, Loader, Text, Select } from '@chaser/components';
import usePush from '../../../../hooks/usePush';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError/index.jsx';
import { useHistory } from 'react-router';
import ConnectService from '../../../../services/connect-service';
import debounce from 'lodash/debounce';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import { pendo } from '../../../../util/logging';

const OdooModal = ({ open, close, onClosed, odooConfiguration, ShortCode }) => {
  const [hasPendingCompaniesFetch, setHasPendingCompaniesFetch] = useState(false);
  const history = useHistory();
  const submittingRef = useRef(false);
  const isNew = !ShortCode;
  const { execute: eConnect } = usePush(
    isNew ? ConnectService.connectOdoo : ConnectService.updateOdoo,
    {
      successToast: 'Successfully connected',
      errorToast: 'Failed to connect',
    },
  );

  const {
    execute: getCompanies,
    loading: getCompaniesLoading,
    error: getCompaniesError,
    response: getCompaniesResponse,
  } = usePush(ConnectService.getOdooCompanies);

  const form = useFormik({
    initialValues: {
      url: odooConfiguration?.url || '',
      db: odooConfiguration?.db || '',
      username: odooConfiguration?.username || '',
      apiKey: '',
      companies: 0,
      companyId: odooConfiguration?.companyId || '',
    },
    validate: ({ url, db, username, apiKey, companies, companyId }) => {
      const errors = {};
      if (!url) errors.url = 'Please enter a URL!';
      if (!db) errors.db = 'Please enter a database name!';
      if (!username) errors.username = 'Please enter a username!';
      if (!apiKey) errors.apiKey = 'Please enter an API key!';
      if (companies > 0 && !companyId) errors.companyId = 'Please select a company!';
      return errors;
    },
    validateOnMount: true,
    onSubmit: async configuration => {
      const config = {
        ...configuration,
        companyId: Number(configuration.companyId),
        companies: undefined,
      };
      const configWithoutCompanyId = {
        ...config,
        companyId: undefined,
      };

      const [errCompanies, resCompanies] = await getCompanies({
        odooConfiguration: configWithoutCompanyId,
      });

      if (errCompanies || !resCompanies.find(company => company.id === configuration.companyId)) {
        return;
      }

      const [errConnect, resConnect] = await eConnect({
        odooConfiguration: config,
        ShortCode,
      });

      if (errConnect) return;

      close();

      pendo.track('integration_modal_submitted', {
        integration: 'odoo',
        isReconnect: !isNew,
      });

      if (resConnect.ShortCode) {
        history.push(`/?organisation=${resConnect.ShortCode}`);
      }

      if (!isNew && resConnect) {
        window.location.reload();
      }
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadCompanies = useCallback(
    debounce(configuration => {
      setHasPendingCompaniesFetch(false);
      if (submittingRef.current) return;
      getCompanies({ odooConfiguration: configuration });
    }, 2000),
    [],
  );

  const configurationForCompanies = useMemo(
    () => JSON.stringify({ ...form.values, companies: undefined, companyId: undefined }),
    [form.values],
  );

  const lastValidConfiguration = useRef('');
  useOnUpdate(() => {
    if (!form.isValid) return;
    if (lastValidConfiguration.current !== configurationForCompanies) {
      lastValidConfiguration.current = configurationForCompanies;
      setHasPendingCompaniesFetch(true);
      loadCompanies(JSON.parse(configurationForCompanies));
    }
  }, [configurationForCompanies, form.isValid]);

  const lastGetCompaniesResponse = useRef(getCompaniesResponse);
  useOnUpdate(() => {
    if (lastGetCompaniesResponse.current === JSON.stringify(getCompaniesResponse)) {
      return;
    }

    lastGetCompaniesResponse.current = JSON.stringify(getCompaniesResponse);
    form.setFieldValue('companyId', '');
    form.setFieldValue('companies', 0);

    if (getCompaniesResponse) {
      form.setFieldValue('companies', getCompaniesResponse.length);

      if (getCompaniesResponse.length === 1) {
        form.setFieldValue('companyId', getCompaniesResponse[0].id);
      }

      if (!isNew) {
        form.setFieldValue('companyId', String(odooConfiguration.companyId));
      }
    }
  }, [getCompaniesResponse]);

  useLayoutEffect(() => {
    submittingRef.current = form.isSubmitting;
  }, [form.isSubmitting]);

  return (
    <Modal title="Odoo Integration" open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Stack gap="medium">
              <LabeledFieldWithError
                name="url"
                as={Input}
                label="URL"
                width="100%"
                placeholder="https://example-company.odoo.com"
              />
              <LabeledFieldWithError
                name="db"
                as={Input}
                label="Database"
                width="100%"
                placeholder="example-company"
              />
              <LabeledFieldWithError
                name="username"
                as={Input}
                label="Username"
                width="100%"
                placeholder="john@example.com"
              />
              <LabeledFieldWithError
                name="apiKey"
                as={Input}
                label="API Key"
                width="100%"
                type="password"
              />
              {getCompaniesResponse?.length > 0 && (
                <LabeledFieldWithError
                  name="companyId"
                  as={Select}
                  label="Company"
                  width="100%"
                  disabled={!isNew}
                >
                  <option value="" disabled>
                    Select a company
                  </option>
                  {getCompaniesResponse.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </LabeledFieldWithError>
              )}
              {(hasPendingCompaniesFetch || getCompaniesLoading) && !form.isSubmitting && (
                <Loader />
              )}
              {!hasPendingCompaniesFetch && getCompaniesError && (
                <Text color="Red">{getCompaniesError}</Text>
              )}
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button
                disabled={
                  hasPendingCompaniesFetch ||
                  getCompaniesLoading ||
                  !form.isValid ||
                  getCompaniesError
                }
                loading={form.isSubmitting}
                type="submit"
                data-trackingid={`integration-odoo-${isNew ? 'connect' : 'update'}`}
              >
                {isNew ? 'Connect' : 'Update'}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default OdooModal;
